import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  TicketPercent,
  BarChart3,
  ArrowLeft,
  ExternalLink,
  CreditCard,
} from "lucide-react";

import { api } from "../../services/api";

/**
 * DeliveryNav — Navegação unificada (topbar) do painel Delivery do empreendedor.
 *
 * Renderiza:
 * - Botão "Voltar" para `/meus-negocios`
 * - Nome do negócio
 * - 4 abas principais (Pedidos / Produtos & Zonas / Cupons / Dashboard)
 * - CTA externo "Ver cardápio público" (auto-resolvido via /api/businesses/{id})
 *
 * O componente busca os slugs do negócio internamente para que TODAS as 4
 * páginas mostrem o link público consistentemente, sem prop drilling em
 * cada consumer.
 *
 * Uso:
 *   <DeliveryNav negocioId="biz_xxx" negocioNome="Hortifruti Teste" />
 *
 * Caller pode opcionalmente passar `publicMenuUrl` para evitar fetch
 * adicional quando já tem os dados em mãos.
 */
export default function DeliveryNav({ negocioId, negocioNome, publicMenuUrl }) {
  const base = `/empreendedor/delivery/${negocioId}`;
  const [resolvedPublicUrl, setResolvedPublicUrl] = useState(publicMenuUrl || null);

  useEffect(() => {
    if (publicMenuUrl) {
      setResolvedPublicUrl(publicMenuUrl);
      return;
    }
    if (!negocioId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/businesses/${encodeURIComponent(negocioId)}`);
        const biz = r.data;
        if (cancelled) return;
        if (biz?.municipio_slug && biz?.slug) {
          setResolvedPublicUrl(`/delivery/${biz.municipio_slug}/${biz.slug}`);
        }
      } catch { /* link público é opcional — silencia */ }
    })();
    return () => { cancelled = true; };
  }, [negocioId, publicMenuUrl]);

  const itens = [
    { to: base, end: true, label: "Pedidos", icon: ShoppingBag, testid: "dnav-pedidos" },
    { to: `${base}/produtos`, label: "Produtos & Zonas", icon: Package, testid: "dnav-produtos" },
    { to: `${base}/cupons`, label: "Cupons", icon: TicketPercent, testid: "dnav-cupons" },
    { to: `${base}/dashboard`, label: "Dashboard", icon: BarChart3, testid: "dnav-dashboard" },
    { to: `${base}/assinatura`, label: "Assinatura", icon: CreditCard, testid: "dnav-assinatura" },
  ];
  return (
    <div className="bg-white border-b border-[var(--border)] sticky top-0 z-30" data-testid="delivery-nav">
      <div className="max-w-7xl mx-auto px-4">
        {/* Topo: voltar + nome + atalho público */}
        <div className="flex items-center gap-3 py-3">
          <Link
            to="/meus-negocios"
            className="p-2 -ml-2 hover:bg-[var(--bg-secondary)] rounded-lg"
            title="Voltar para Meus Negócios"
            data-testid="dnav-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shrink-0">
              <LayoutDashboard className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)] leading-none">
                Empreende Delivery
              </p>
              <h1 className="text-sm font-bold truncate">{negocioNome || "Meu Negócio"}</h1>
            </div>
          </div>
          {resolvedPublicUrl && (
            <a
              href={resolvedPublicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)]"
              data-testid="dnav-public-link"
            >
              <ExternalLink className="w-3 h-3" /> Cardápio público
            </a>
          )}
        </div>
        {/* Tabs */}
        <nav className="flex gap-1 overflow-x-auto -mb-px" aria-label="Seções do Delivery">
          {itens.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? "border-orange-500 text-orange-600 font-semibold"
                    : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border)]"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
